<template>
  <div
    :class="containerClasses"
    :aria-label="label"
    role="status"
  >
    <svg
      :class="spinnerClasses"
      :style="spinnerStyle"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Circle background -->
      <circle
        cx="12"
        cy="12"
        r="10"
        :stroke="trackColor"
        :stroke-width="strokeWidth"
        class="opacity-25"
      />
      
      <!-- Spinning arc -->
      <path
        :stroke="color"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        :d="arcPath"
        class="opacity-75"
      >
        <animateTransform
          attributeName="transform"
          attributeType="XML"
          type="rotate"
          :dur="duration"
          from="0 12 12"
          to="360 12 12"
          repeatCount="indefinite"
        />
      </path>
    </svg>

    <!-- Optional text -->
    <span v-if="text || $slots.default" :class="textClasses">
      <slot>{{ text }}</slot>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  color?: string
  trackColor?: string
  strokeWidth?: number
  text?: string
  label?: string
  inline?: boolean
  centered?: boolean
  overlay?: boolean
  duration?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  color: 'currentColor',
  trackColor: 'currentColor',
  strokeWidth: 2,
  label: 'Loading...',
  inline: false,
  centered: false,
  overlay: false,
  duration: '1s'
})

const sizeClasses = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12'
}

const textSizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl'
}

const containerClasses = computed(() => {
  const baseClasses = ['spinner-container']

  if (props.inline) {
    baseClasses.push('inline-flex', 'items-center', 'space-x-2')
  } else {
    baseClasses.push('flex', 'flex-col', 'items-center', 'space-y-2')
  }

  if (props.centered && !props.overlay) {
    baseClasses.push('justify-center', 'w-full', 'h-full')
  }

  if (props.overlay) {
    baseClasses.push(
      'fixed',
      'inset-0',
      'z-50',
      'bg-white',
      'bg-opacity-75',
      'backdrop-blur-sm',
      'flex',
      'items-center',
      'justify-center'
    )
  }

  return baseClasses
})

const spinnerClasses = computed(() => [
  'animate-spin',
  sizeClasses[props.size]
])

const textClasses = computed(() => [
  'text-gray-600',
  'font-medium',
  textSizeClasses[props.size]
])

const spinnerStyle = computed(() => ({
  color: props.color
}))

const arcPath = computed(() => {
  // Create a quarter circle arc for the spinning indicator
  const radius = 10
  const startAngle = 0
  const endAngle = Math.PI / 2 // 90 degrees
  
  const startX = 12 + radius * Math.cos(startAngle)
  const startY = 12 + radius * Math.sin(startAngle)
  const endX = 12 + radius * Math.cos(endAngle)
  const endY = 12 + radius * Math.sin(endAngle)
  
  return `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`
})
</script>

<style scoped>
.spinner-container {
  @apply select-none;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style> 